import { IcmService, IcmTeam, useORPCClient } from "Core/oRPC";
import { MaxSearchResultCount, universalSearchResultItem, useUniversalSearch } from "Shared/UniversalSearch";
import { useCallback } from "react";

import { getTeamLink } from "Apps/Service/ServiceRoutes";
import { getTeamOwnedServiceNames } from "./Hooks/TeamServiceTreeMappings.utils";
import { useNavigate } from "react-router";
import { useWorkloadV2 } from "Shared/Hooks/Core/useWorkloadV2";
import { AppRegistry } from "Apps/AppRegistry";

const searchCount = 30;

export const useSearchTeams = (loggedIn?: boolean) => {
    const navigate = useNavigate();

    const { icmKustoApi, oncallApi } = useORPCClient();
    const { getWorkloadByService, workloads } = useWorkloadV2(loggedIn);

    const searchTeams = useCallback(
        async (word: string) => {
            if (!word) {
                return [];
            }
            const allCategoryIds = [
                ...new Set(
                    (workloads ?? []).flatMap((workload) =>
                        workload.IcmServices.filter((service) => service.IsIcmServiceCategory).map((service) => service.IcmId.toString())
                    )
                )
            ];
            const allServiceIds = [...new Set((workloads ?? []).flatMap((workload) => workload.IcmServices.map((service) => service.IcmId.toString())))];
            const resultList: universalSearchResultItem[] = [];
            let teams: IcmTeam[] = await icmKustoApi.postSearchTeamsInServiceCategoryIds({
                body: allCategoryIds,
                query: { searchWord: word, topN: searchCount }
            });
            if (teams.length < searchCount) {
                const moreTeams = await icmKustoApi.postSearchTeamsInServices({
                    body: allServiceIds,
                    query: { keyword: word, topN: searchCount }
                });
                teams = teams.concat(moreTeams.slice(0, searchCount - teams.length));
            }
            teams = teams.sort((a, b) => {
                const aIndex = a.Name?.toLowerCase().indexOf(word.toLowerCase()) ?? -1;
                const bIndex = b.Name?.toLowerCase().indexOf(word.toLowerCase()) ?? -1;
                if (aIndex === -1) {
                    return 1;
                }
                if (bIndex === -1) {
                    return -1;
                }
                if (aIndex === bIndex) {
                    return a.Name?.localeCompare(b.Name ?? "") ?? 0;
                }
                return aIndex - bIndex;
            });

            const services = await icmKustoApi.postGetServiceFromTeamPublicIds(teams.map((t) => t.PublicId ?? ""));
            const teamsWorkloadMap = new Map<string, IcmService | undefined>();
            Object.entries(services).forEach(([teamPublicId, service]) => {
                teamsWorkloadMap.set(teamPublicId, service);
            });

            const teamServiceMappingResults = await Promise.allSettled(
                [...new Set(teams.map((team) => Number(team.Id)).filter((teamId) => Number.isInteger(teamId)))].map(async (teamId) => {
                    const mappings = await oncallApi.getTeamsv2GetTeamServiceTreeMappings({ teamId });
                    return [teamId.toString(), getTeamOwnedServiceNames(mappings)] as const;
                })
            );
            const teamServiceMappings = teamServiceMappingResults.flatMap((result) => (result.status === "fulfilled" ? [result.value] : []));
            const teamServiceMap = new Map(teamServiceMappings);

            teams.forEach((team) => {
                const service = teamsWorkloadMap.get(team.PublicId ?? "");
                if (team.Id && team.Name && team.OwningService && team.OwningService.Name && team.PublicId && service) {
                    const link = getTeamLink(team.Id);
                    const workloadName = getWorkloadByService(service)?.DisplayName ?? "";
                    const ownedServices = teamServiceMap.get(team.Id) ?? [];
                    const ownedServicesDescription = ownedServices.length > 0 ? `Services: ${ownedServices.join(", ")}` : "";
                    const teamDescription = team.Description?.trim() ?? "";
                    const descriptionParts = [workloadName, ownedServicesDescription, teamDescription].filter((part) => part.length > 0);
                    resultList.push({
                        id: team.Id,
                        title: team.OwningService.Name + "\\" + team.Name,
                        description: descriptionParts.join(" — "),
                        onClick: () => navigate(link),
                        navigationTarget: link
                    });
                }
            });
            return resultList.length > MaxSearchResultCount ? resultList.slice(0, MaxSearchResultCount) : resultList;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [getWorkloadByService, navigate, workloads]
    );

    useUniversalSearch("Teams", "teams", searchTeams, false, AppRegistry.Teams.path);
};
